import React, { memo } from 'react'
import { useSelector } from 'react-redux'
import { IComment, RootState } from 'constants/interfaces'
import { useUserName } from 'hooks/useUserName'
import { Flex, IconButton, Image, Text, Box } from '@chakra-ui/core'
import FilePreview from 'components/CommonComponents/FilePreview'
import { VideoPreview } from 'components/CommonComponents/VideoPreview'
import { isRtlSelector } from 'redux/selectors/general'
import { getTextDirection } from 'utils'

export const MessagePreview = memo(({ comment, onClose }: { comment: IComment; onClose: () => void }) => {
  const { message, images, videos, files, created_by } = comment
  const { uid } = useSelector((state: RootState) => state.auth)
  const createdByUserName = useUserName(created_by)
  const isRtl = useSelector(isRtlSelector)
  const isCurrentUser = uid === created_by

  const allMedia = [
    ...(images?.map((img) => ({ type: 'image', src: img })) || []),
    ...(videos?.map((vid) => ({ type: 'video', src: vid })) || []),
    ...(files?.map((file) => ({ type: 'file', src: file })) || []),
  ]

  const previewItems = allMedia.slice(0, 3)
  const remainingCount = allMedia.length - 3

  return (
    <Flex w="100%" justifyContent="space-between" alignItems="center" style={{ gap: '8px' }}>
      <Flex
        overflow="hidden"
        alignItems="center"
        justifyContent="space-between"
        borderRadius="10px"
        border="1px solid #f3f3f3"
        w="100%"
        boxShadow="md"
      >
        <Flex alignItems="center">
          <Box
            width="5px"
            height="100%"
            minHeight="65px"
            backgroundColor={isCurrentUser ? 'blue.400' : 'pink.400'}
            mr={isRtl ? 0 : 2}
            ml={isRtl ? 2 : 0}
          />
          <Flex flexDir="column">
            <Text fontSize="sm" color="gray.600" maxW="300px" isTruncated>
              @{createdByUserName}
            </Text>
            <Text fontSize="sm" maxW="400px" isTruncated dir={getTextDirection(message)}>
              {message}
            </Text>
          </Flex>
        </Flex>
        <Flex style={{ gap: '5px' }} px={2}>
          {previewItems.length > 0 &&
            previewItems.map((item, index) => (
              <Box key={index} borderRadius="10px" w="50px" h="50px" overflow="hidden" position="relative">
                {item.type === 'image' && (
                  <Image src={item.src} alt="image" width="100%" height="100%" objectFit="cover" />
                )}
                {item.type === 'video' && <VideoPreview url={item.src} />}
                {item.type === 'file' && (
                  <FilePreview
                    fileUrl={item.src}
                    width="50px"
                    height="50px"
                    textProps={{ fontSize: '6px', maxW: '50px', isTruncated: true, maxH: '40px' }}
                    iconProps={{ size: 20 }}
                  />
                )}
              </Box>
            ))}
          {remainingCount > 0 && (
            <Flex
              width="50px"
              height="50px"
              borderRadius="10px"
              bg="gray.100"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontSize="sm" color="gray.500">
                +{remainingCount}
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
      <IconButton
        aria-label="Close reply"
        borderRadius="full"
        icon="close"
        size="xs"
        variant="ghost"
        mx="10px"
        onClick={onClose}
      />
    </Flex>
  )
})
